// CAT Mock 7 — VARC section.
const catMock7Varc = [
  {
    id: 1,
    type: "mcq",
    passageId: "rc1",
    passage: `Until the middle of the nineteenth century, every town kept its own time. Noon was the moment the sun stood highest over the local church or town hall, and since the sun crosses the sky from east to west, noon in a western town arrived several minutes after noon in an eastern one. For people who travelled no faster than a horse, the difference hardly mattered: a traveller's watch drifted out of step with local clocks so slowly that it could be corrected on arrival.

The railways ended this comfortable arrangement. A train that crossed a country in a few hours passed through dozens of local times, and timetables that tried to honour them all became almost unreadable. Worse, on single-track lines where trains running in opposite directions had to pass each other at sidings, a confusion about the time could be fatal. Railway companies responded by adopting a single standard time along their lines, usually that of the capital or of the company's headquarters, and the telegraph made it possible to send that time down the line to every station.

What began as a convenience for railway companies soon became the time of daily life. Towns found it awkward to keep one time on the station clock and another on the church tower, and over several decades local time gave way, sometimes after bitter protest. Critics complained that a distant authority was dictating the hour, and that clocks no longer told the truth about the sun. They were right on both counts. Standard time is a social agreement rather than a natural fact; its value lies not in its accuracy but in the fact that everyone shares it.`,
    question: "According to the passage, local time posed little difficulty before the railways because:",
    options: [
      "A. most towns were located at the same longitude.",
      "B. travel was slow enough that a traveller could adjust their watch on arrival without inconvenience.",
      "C. the telegraph allowed towns to coordinate their clocks.",
      "D. people did not own watches."
    ],
    answer: "B"
  },
  {
    id: 2,
    type: "mcq",
    passageId: "rc1",
    question: "The author mentions single-track lines in order to:",
    options: [
      "A. show that confusion over time could have dangerous consequences for railways.",
      "B. explain why railway companies chose the time of the capital.",
      "C. argue that railways were built too cheaply.",
      "D. illustrate how the telegraph transmitted time signals."
    ],
    answer: "A"
  },
  {
    id: 3,
    type: "mcq",
    passageId: "rc1",
    question: "The author's attitude towards the critics of standard time can best be described as:",
    options: [
      "A. contemptuous, because their objections were based on ignorance.",
      "B. sympathetic, because standard time was eventually abandoned.",
      "C. indifferent, because the debate is of no present interest.",
      "D. conceding that their observations were accurate, while holding that they missed the point of standard time."
    ],
    answer: "D"
  },
  {
    id: 4,
    type: "mcq",
    passageId: "rc1",
    question: "Which of the following can be inferred from the passage?",
    options: [
      "A. Railway companies were opposed to the adoption of standard time by towns.",
      "B. Local time was more accurate than standard time in every respect.",
      "C. Without a means of sending time signals rapidly over long distances, a uniform railway time would have been harder to maintain.",
      "D. Standard time was adopted by all towns at the same moment."
    ],
    answer: "C"
  },
  {
    id: 5,
    type: "mcq",
    passageId: "rc2",
    passage: `On a still summer evening, the centre of a large city can be several degrees warmer than the countryside around it. The effect, known as the urban heat island, has a simple cause. Asphalt, brick and concrete absorb sunlight during the day and release it slowly after dark, while the narrow spaces between tall buildings trap that heat and block the breezes that might carry it away. Air-conditioners, cars and factories add heat of their own. And because cities have replaced soil and vegetation with hard surfaces, they lose the cooling that plants provide by drawing water from the ground and releasing it as vapour through their leaves.

The consequences are more serious than discomfort. During heatwaves, deaths rise sharply, and they rise most in the hottest neighbourhoods, which are often the poorest: districts with little greenery, crowded housing and fewer people who can afford to cool their homes. Higher night-time temperatures matter particularly, because a body that cannot cool down at night enters the next day already under strain.

Cities have begun to respond. Some paint roofs white to reflect sunlight; others plant trees along streets and in car parks, or require new buildings to include green roofs. Trees are the most popular remedy, and for good reason: a mature tree shades the ground and cools the air around it. But trees take decades to grow, need water that some cities lack, and are easiest to plant in neighbourhoods that already have space and money. A programme that plants most of its trees where planting is simplest may end up cooling the districts that least need it.`,
    question: "Which of the following is NOT mentioned in the passage as a cause of the urban heat island?",
    options: [
      "A. Building materials that store heat and release it at night.",
      "B. Heat produced by vehicles and machines.",
      "C. Air pollution that traps heat in the atmosphere above the city.",
      "D. The loss of the cooling effect of vegetation."
    ],
    answer: "C"
  },
  {
    id: 6,
    type: "mcq",
    passageId: "rc2",
    question: "According to the passage, high night-time temperatures are especially dangerous because:",
    options: [
      "A. people who cannot cool down at night begin the following day already under physical strain.",
      "B. air-conditioners are switched off at night.",
      "C. most heat-related deaths occur after dark.",
      "D. trees release heat at night."
    ],
    answer: "A"
  },
  {
    id: 7,
    type: "mcq",
    passageId: "rc2",
    question: "The author's concern in the final sentence is that tree-planting programmes may:",
    options: [
      "A. use too much water.",
      "B. take too long to show results.",
      "C. be more expensive than painting roofs white.",
      "D. benefit wealthier areas more than the poorer, hotter districts that need cooling most."
    ],
    answer: "D"
  },
  {
    id: 8,
    type: "mcq",
    passageId: "rc2",
    question: "The overall tone of the passage is best described as:",
    options: [
      "A. alarmist, predicting that cities will soon become uninhabitable.",
      "B. explanatory and measured, endorsing remedies while noting their limitations.",
      "C. dismissive of attempts to cool cities.",
      "D. enthusiastic about green roofs as the only real solution."
    ],
    answer: "B"
  },
  {
    id: 9,
    type: "mcq",
    passageId: "rc3",
    passage: `Lists of "untranslatable words" are perennially popular. They offer a Portuguese word for a longing for something absent, a Danish word for a particular cosy contentment, a Japanese word for the sunlight that filters through leaves, and invite the reader to marvel that other languages have captured experiences for which English has no name. The implication is that each language carves up the world differently, and that its speakers notice what others miss.

Translators tend to greet such lists with weary patience. The words in question are not, in fact, untranslatable; the list itself translates them, usually in a single sentence. What they lack is a one-word equivalent, which is a different and far more common condition. Every language combines meanings into single words in its own way, and an English speaker who learns that German has a compound for "joy at another's misfortune" has learnt something about German word formation, not about the emotional range of Germans, who are no more prone to malice than anyone else.

The real difficulties of translation lie elsewhere — not in isolated words but in rhythm, register, allusion and the web of associations that a phrase carries for its native readers. A line of poetry that echoes a well-known proverb, or a joke that depends on a pun, resists translation far more stubbornly than any exotic noun. The fascination with untranslatable words is harmless enough, but it directs attention to the place where translation is easiest to admire and away from the places where it is hardest to do.`,
    question: "According to the author, the words found on lists of \"untranslatable words\" are better described as words that:",
    options: [
      "A. have no meaning outside their own culture.",
      "B. are rarely used by native speakers.",
      "C. have no single-word equivalent in English.",
      "D. are impossible to explain in another language."
    ],
    answer: "C"
  },
  {
    id: 10,
    type: "mcq",
    passageId: "rc3",
    question: "The example of the German compound for \"joy at another's misfortune\" is used to make the point that:",
    options: [
      "A. a language's vocabulary does not reliably reveal the emotional character of its speakers.",
      "B. German speakers are especially prone to malice.",
      "C. German is harder to translate than other languages.",
      "D. compound words are a feature unique to German."
    ],
    answer: "A"
  },
  {
    id: 11,
    type: "mcq",
    passageId: "rc3",
    question: "According to the passage, which of the following would most likely pose the greatest challenge to a translator?",
    options: [
      "A. A technical term with a precise definition.",
      "B. A noun for an object found only in the source culture.",
      "C. A word that has a one-word equivalent in the target language.",
      "D. A line of verse whose effect depends on echoing a familiar proverb."
    ],
    answer: "D"
  },
  {
    id: 12,
    type: "mcq",
    passageId: "rc3",
    question: "The author's main criticism of the popularity of untranslatable words is that it:",
    options: [
      "A. encourages people to learn foreign languages for the wrong reasons.",
      "B. focuses attention on an easy aspect of translation while neglecting the genuinely difficult ones.",
      "C. spreads false information about the meanings of foreign words.",
      "D. is offensive to native speakers of the languages concerned."
    ],
    answer: "B"
  },
  {
    id: 13,
    type: "mcq",
    passageId: "rc4",
    passage: `The placebo effect has long been treated as a nuisance by medical researchers. Patients given an inert pill in a clinical trial often report feeling better, and a new drug must outperform this improvement to be judged effective. For most of the twentieth century, the effect was assumed to depend on deception: patients improved because they believed, wrongly, that they were receiving an active treatment.

That assumption has been challenged by a series of curious experiments. In them, patients with conditions such as chronic back pain or irritable bowel syndrome were handed pills and told plainly that the pills contained no medicine. They were told only that placebos had produced improvements in other trials, perhaps by engaging the body's own responses to the ritual of treatment. Many of these patients nevertheless reported less pain than those who received no pills at all. The results suggest that the placebo effect is not simply a matter of false belief, but owes something to expectation, attention and the act of taking care of oneself.

Researchers urge caution. The conditions studied are those in which symptoms are reported by the patient and fluctuate over time, and there is little evidence that placebos shrink tumours or cure infections. Patients who know they are receiving a placebo also know which group they are in, which makes the trials harder to control. Still, the findings raise an uncomfortable question for medicine. If part of what makes treatment work is the encounter itself — the time a doctor spends listening, the confidence with which care is offered — then a health system that measures efficiency in minutes per patient may be discarding something of real value.`,
    question: "The experiments described in the second paragraph challenge the view that:",
    options: [
      "A. placebos can produce any improvement at all.",
      "B. chronic back pain can be treated with medicine.",
      "C. the placebo effect depends on patients being misled about their treatment.",
      "D. clinical trials need a control group."
    ],
    answer: "C"
  },
  {
    id: 14,
    type: "mcq",
    passageId: "rc4",
    question: "The author mentions tumours and infections in order to:",
    options: [
      "A. indicate the limits of what the placebo findings appear to show.",
      "B. argue that placebos are more effective than real drugs for serious diseases.",
      "C. criticise researchers for neglecting serious illnesses.",
      "D. show that placebos are dangerous."
    ],
    answer: "A"
  },
  {
    id: 15,
    type: "mcq",
    passageId: "rc4",
    question: "Which of the following, if true, would most weaken the conclusion drawn from the experiments in the second paragraph?",
    options: [
      "A. The patients in the experiments had suffered from their conditions for many years.",
      "B. Patients who received no pills were told that they were in the control group.",
      "C. Similar results were obtained in several different countries.",
      "D. Most patients given the open placebos privately believed that the pills secretly contained medicine."
    ],
    answer: "D"
  },
  {
    id: 16,
    type: "mcq",
    passageId: "rc4",
    question: "The \"uncomfortable question\" raised in the final paragraph concerns whether:",
    options: [
      "A. doctors should prescribe placebos instead of drugs.",
      "B. reducing the time and attention given to patients for the sake of efficiency sacrifices part of what makes treatment effective.",
      "C. clinical trials should be abolished.",
      "D. patients should be told the truth about their treatment."
    ],
    answer: "B"
  },
  {
    id: 17,
    type: "tita",
    question: "The four sentences (labelled 1, 2, 3, 4) below, when properly sequenced, form a coherent paragraph. Key in the sequence of the four numbers as your answer.\n1. Their solution was to lay down a fixed grid of lines — the meridians and parallels — that could be drawn before any coast was surveyed.\n2. Early mapmakers faced a basic problem: how to place newly discovered coasts in correct relation to each other.\n3. A sailor who could measure his latitude and longitude could then mark his position on this grid, even on a blank sheet.\n4. Measuring longitude at sea, however, remained unreliable until accurate clocks were developed in the eighteenth century.",
    answer: "2134"
  },
  {
    id: 18,
    type: "tita",
    question: "The four sentences (labelled 1, 2, 3, 4) below, when properly sequenced, form a coherent paragraph. Key in the sequence of the four numbers as your answer.\n1. The sugar was then crushed out of the stalks and boiled down in large copper vats.\n2. Cane was cut by hand at the height of the harvest, when its sugar content peaked.\n3. What remained after boiling was a dark crystalline mass, which was shipped to Europe for refining.\n4. Speed mattered, because cut cane begins to lose its sugar within a day.",
    answer: "2413"
  },
  {
    id: 19,
    type: "tita",
    question: "The five sentences (labelled 1, 2, 3, 4, 5) below, when properly sequenced, form a coherent paragraph. Key in the sequence of the five numbers as your answer.\n1. Bees visiting the flowers pick up pollen and carry it to the next flower of the same kind.\n2. Many plants cannot fertilise themselves and depend on animals to carry pollen between flowers.\n3. In return for this service, the plant rewards the bee with nectar.\n4. To attract these animals, flowers advertise themselves with bright colours and strong scents.\n5. Such partnerships are so ancient that many flowers and their pollinators have evolved to fit one another closely.",
    answer: "24135"
  },
  {
    id: 20,
    type: "mcq",
    question: "The passage given below is followed by four summaries. Choose the option that best captures the essence of the passage.\nAs incomes rise, households tend to spend a smaller share of their budget on food, even as they spend more on it in absolute terms. A family that doubles its income does not double its consumption of bread; it buys better bread, eats out more often, and devotes the rest of its additional income to housing, education and leisure. For this reason, the share of income spent on food is often used by economists as a rough measure of a country's prosperity.",
    options: [
      "A. Richer families eat more bread than poorer ones.",
      "B. As incomes rise, food takes a declining share of household spending, which makes that share a useful rough indicator of prosperity.",
      "C. Economists measure prosperity only by spending on food.",
      "D. Families that earn more spend less money on food."
    ],
    answer: "B"
  },
  {
    id: 21,
    type: "mcq",
    question: "The passage given below is followed by four summaries. Choose the option that best captures the essence of the passage.\nCities that build new roads to relieve congestion often find that traffic soon returns to its former level. The new capacity makes driving more attractive, so people who previously travelled at other times, by other routes or by other means begin to drive at peak hours. Traffic engineers call this induced demand. It does not mean that new roads are never worth building, but it does mean that they rarely deliver the lasting relief from congestion that is promised.",
    options: [
      "A. Building new roads always makes congestion worse.",
      "B. Traffic engineers oppose the construction of new roads.",
      "C. Public transport is the only solution to congestion.",
      "D. Because extra road capacity attracts more drivers, new roads seldom provide lasting relief from congestion, though they may still be justified."
    ],
    answer: "D"
  },
  {
    id: 22,
    type: "mcq",
    question: "The passage given below is followed by four summaries. Choose the option that best captures the essence of the passage.\nMemory is less like a recording than a reconstruction. Each time we recall an event, we rebuild it from fragments, filling the gaps with what seems plausible, and the rebuilt version is what we store for next time. This is why confident eyewitnesses can be sincerely wrong, and why a memory can change over years without its owner noticing. The process is not a defect but a by-product of a system designed to extract general lessons rather than preserve exact detail.",
    options: [
      "A. Eyewitnesses usually lie about what they have seen.",
      "B. Human memory is defective and cannot be trusted at all.",
      "C. Because memories are rebuilt each time they are recalled, they can change and mislead, a consequence of a system geared to learning lessons rather than storing exact records.",
      "D. Memories become more accurate the more often they are recalled."
    ],
    answer: "C"
  },
  {
    id: 23,
    type: "tita",
    question: "Five jumbled sentences (labelled 1, 2, 3, 4, 5), related to a topic, are given below. Four of them can be put together to form a coherent paragraph. Identify the odd sentence and key in the number of that sentence as your answer.\n1. Coral reefs are built by tiny animals that secrete skeletons of calcium carbonate.\n2. Over thousands of years, these skeletons accumulate into vast structures that shelter a quarter of all marine species.\n3. Many tourists travel long distances to dive among the reefs.\n4. The corals depend on algae living in their tissues, which supply them with food through photosynthesis.\n5. When the water becomes too warm, the corals expel these algae and turn white, a process known as bleaching.",
    answer: "3"
  },
  {
    id: 24,
    type: "tita",
    question: "Five jumbled sentences (labelled 1, 2, 3, 4, 5), related to a topic, are given below. Four of them can be put together to form a coherent paragraph. Identify the odd sentence and key in the number of that sentence as your answer.\n1. The printing press made books far cheaper to produce than hand-copied manuscripts.\n2. As prices fell, books reached readers who could never before have afforded them.\n3. Paper had been invented in China many centuries earlier.\n4. This wider readership in turn created demand for books in everyday languages rather than Latin.\n5. Printers responded by publishing more works in the languages that ordinary people spoke.",
    answer: "3"
  }
];

export default catMock7Varc;
